
import React, { useState, useCallback } from 'react';
import { useOptimizedChat } from '@/hooks/useOptimizedChat';
import { ChatHeader } from './ChatHeader';
import { MessageListWrapper } from './MessageListWrapper';
import { UltraSimpleChatInput } from './UltraSimpleChatInput';
import { ChatLoadingState } from './states/ChatLoadingState';
import { DeletedChatIndicator } from './DeletedChatIndicator';
import { SelfVerificationBanner } from './SelfVerificationBanner';
import { UnverifiedPartnerBanner } from './UnverifiedPartnerBanner';
import { Button } from '@/components/ui/button';
import { RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import ChatList from '@/pages/main/ChatList';
import NavItems from '@/components/navigation/NavItems';
import { OnboardingGuard } from '@/components/guards/OnboardingGuard';
import { businessRulesEngine } from '@/core/BusinessRulesEngine';

export const UltraSimpleChatScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user, profileData } = useAuth();
  const isMobile = useIsMobile();
  
  const {
    conversation,
    messages,
    partner,
    mediaMessages,
    loading,
    replyingTo,
    hasOlderMessages,
    loadingOlderMessages,
    isReactivating,
    sendText,
    sendImage,
    sendAudio,
    deleteMessage,
    reactivateConversation,
    loadOlderMessages,
    setReplyingTo
  } = useOptimizedChat();

  // State for icebreaker suggestion
  const [icebreakerText, setIcebreakerText] = useState<string>('');

  const handleSelectIcebreaker = useCallback((text: string) => {
    setIcebreakerText(text);
  }, []);

  const handleClearIcebreaker = useCallback(() => {
    setIcebreakerText('');
  }, []);
  // Create wrapper functions to match expected signatures
  const handleReply = (messageId: string, content: string, senderId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (message) {
      setReplyingTo({
        id: message.id,
        content: message.content || '',
        sender_id: message.sender_id,
        content_type: message.content_type
      });
    }
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const handleSendAudio = async (audioBlob: Blob, duration: number): Promise<boolean> => {
    return await sendAudio(audioBlob, duration);
  };

  const handleReactivateConversation = async () => {
    await reactivateConversation();
  };

  // Verificar se usuário está verificado
  const verificationCheck = profileData 
    ? businessRulesEngine.validateProfileVerified(profileData)
    : { allowed: false };

  const isUserVerified = verificationCheck.allowed;
  const isPartnerVerified = partner?.verification_level === 'full';

  // Verificar se deve mostrar os banners
  const showSelfBanner = !isUserVerified;
  const showPartnerBanner = !isPartnerVerified;

  if (loading) {
    return <ChatLoadingState username={partner?.username} onRetry={() => window.location.reload()} />;
  }

  if (!partner) {
    navigate('/main/chats');
    return null;
  }

  return (
    <OnboardingGuard>
      {/* Logotipo fixado no canto superior esquerdo - mesmo critério da lista de chats */}
      {!isMobile && (
        <div className="fixed top-0 left-0 z-50 bg-black/95 backdrop-blur-sm border-b border-white/10 border-r border-white/10 p-4 h-16 flex items-center w-80">
          <h1 
            className="font-serif text-xl font-bold cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/main/feed')}
          >
            <span className="text-white">Inter</span>
            <span className="text-luxury-red">laced</span>
          </h1>
        </div>
      )}

      <div className="flex h-screen bg-black overflow-hidden">
      {/* Lista de conversas - só aparece em telas não mobile */}
      {!isMobile && (
        <div className="w-80 h-full border-r border-white/10 flex-shrink-0 overflow-hidden bg-black">
          <ChatList variant="sidebar" showHeader={false} />
        </div>
      )}

      {/* Area do chat */}
      <div className={`flex-1 h-full flex flex-col overflow-hidden ${isMobile ? 'fixed inset-0 z-50' : ''}`}>
        {/* Header fixo */}
        <ChatHeader
          partner={partner}
          conversationId={conversation?.id}
          conversation={null}
          messages={messages}
          mediaMessages={mediaMessages}
          realtimeConnected={true}
          conversationDeletedAt={conversation?.deleted_at}
          conversationDeletedBy={conversation?.deleted_by}
          isReactivating={isReactivating}
          onReactivate={handleReactivateConversation}
        />

        {/* Spacer para compensar header fixo */}
        <div className="h-20 flex-shrink-0" />
        
        {/* Banners de verificação - no fluxo normal */}
        <div className="flex-shrink-0">
          {showSelfBanner && (
            <SelfVerificationBanner partnerUsername={partner.username} />
          )}
          {showPartnerBanner && (
            <UnverifiedPartnerBanner partnerUsername={partner.username} />
          )}
        </div>

        {/* Messages - ocupa todo o espaço restante */}
        <div className="flex-1 overflow-hidden mb-20">
          <MessageListWrapper
              messages={messages}
              currentUserId={user?.id || ''}
              partner={partner}
              hasOlderMessages={hasOlderMessages}
              loadingOlderMessages={loadingOlderMessages}
              onReply={handleReply}
              onDelete={deleteMessage}
              onLoadOlderMessages={loadOlderMessages}
              onSelectIcebreaker={handleSelectIcebreaker}
            />
        </div>

        {/* Indicador de conversa deletada */}
        {conversation?.deleted_at && (
          <div className={`fixed bottom-0 left-0 right-0 ${!isMobile ? 'left-80' : 'lg:left-64'} bg-red-900/20 border-t border-red-500/30 p-4`}>
            <DeletedChatIndicator
              partnerName={partner?.username || 'Usuário'}
              deletedByCurrentUser={conversation.deleted_by === user?.id}
              deletedByPartner={conversation.deleted_by === partner?.id}
            />
            {conversation.deleted_by === user?.id && (
              <div className="mt-3 flex justify-center">
                <Button
                  onClick={handleReactivateConversation}
                  disabled={isReactivating}
                  variant="outline"
                  size="sm"
                  className="bg-green-600/20 border-green-500/50 text-green-400 hover:bg-green-600/30 disabled:opacity-50"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {isReactivating ? 'Reativando...' : 'Reativar Conversa'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Input fixo no bottom - só renderizar se conversa não estiver deletada */}
        {!conversation?.deleted_at && (
          <UltraSimpleChatInput
            onSendText={sendText}
            onSendImage={sendImage}
            onSendAudio={handleSendAudio}
            replyingTo={replyingTo}
            onCancelReply={handleCancelReply}
            externalValue={icebreakerText}
            onExternalValueClear={handleClearIcebreaker}
          />
        )}

        {/* Navigation fixo no canto inferior esquerdo - apenas md e lg */}
        {!isMobile && (
          <div className="fixed bottom-0 left-0 w-80 bg-black border-t border-white/10 z-30">
            <NavItems isMobile={true} onItemClick={() => {}} />
          </div>
        )}
      </div>
      </div>
    </OnboardingGuard>
  );
};
